<template>
  <div class="toolbox-column">
    <div class="toolbox-menu" :class="{ 'toolbox-menu--collapsed': !expanded }">
      <div class="toolbox-menu__header" :class="{ 'toolbox-menu__header--collapsed': !expanded }">
        <ButtonSquare
          class="toolbox-menu__header__toggle-button"
          :imgSource="buttonImgSource"
          :activeState="
            (isMeasurementActive && !expanded) ||
            (preventButtonInactive && !expanded) ||
            (isDataComparisonToolActive && !expanded && isMapviewShowing)
              ? 'active'
              : 'inactive'
          "
          @clicked="onToolButtonClick"
        />
        <div v-show="expanded" class="toolbox-menu__header__tool-buttons">
          <ButtonSquare
            :activeState="
              currentTool === 'linemeasurement' || measurementTypeCleared === 'linemeasurement'
                ? 'active'
                : 'inactive'
            "
            :imgSource="measureLineIconSource"
            @clicked="onLineMeasureButtonClick"
          />
          <ButtonSquare
            :activeState="
              currentTool === 'areameasurement' || measurementTypeCleared === 'areameasurement'
                ? 'active'
                : 'inactive'
            "
            :imgSource="measureAreaIconSource"
            @clicked="onAreaMeasureButtonClick"
          />
          <ButtonSquare
            v-show="isMapviewShowing"
            :activeState="currentTool === 'datacomparisontool' ? 'active' : 'inactive'"
            :imgSource="dataComparisonIconSource"
            @clicked="onDataComparisonToolClick"
          />
        </div>
      </div>
      <div v-show="expanded" class="toolbox-menu__content">
        <!-- If placed together with tool buttons, toolbox menu items goes here -->
      </div>
    </div>
    <div
      v-if="
        (filteredDrawings.filter((drawing) => drawing.visible).length === 1 &&
          selectedLevel &&
          !isMapviewShowing) ||
        (filteredDrawings.filter((drawing) => drawing.visible).length === 1 &&
          !selectedLevel &&
          !isPlanviewShowing)
      "
      class="toolbox-menu"
    >
      <ToolboxMenuItem
        id="toolbox-menu-drawing-transparency-slider"
        class="toolbox-menu-item-single"
        :slimMobile="true"
        :title="t('general__text__drawing-opacity')"
      >
        <RangeSliderSingle
          class="range-slider"
          :startValue="currentDrawingTransparency * 100"
          @rangeSliderValue="rangeSliderValue"
        />
      </ToolboxMenuItem>
    </div>
    <div
      v-if="
        isSensordataShowing &&
        currentSensordataType === 'presence' &&
        heatmapDateArray != null &&
        heatmapDateArray.timeStamps.length
      "
      class="toolbox-menu"
    >
      <ToolboxMenuItem class="toolbox-menu-item-single" title="Heatmap">
        <CustomRangeSlider
          class="range-slider"
          :startValue="heatmapDateArray.timeStamps.length - 1"
          :min="0"
          :max="heatmapDateArray.timeStamps.length - 1"
          :displayValues="heatmapDateArray.timeStamps"
          :subtitlePrefix="t('general__text__heatmap_time')"
          @rangeSliderValue="onChangeHeatmapDate"
        />
      </ToolboxMenuItem>
      <ToolboxMenuItem
        class="toolbox-menu-item-single__additional"
        :title="t('general__text__heatmap_intensity')"
      >
        <RangeSliderSingle
          class="range-slider"
          :startValue="currentHeatmapIntensity * 100"
          @rangeSliderValue="changeHeatmapIntensity"
        />
      </ToolboxMenuItem>
    </div>
    <div v-if="isMeasurementActive || preventButtonInactive" class="toolbox-menu">
      <ToolboxMenuItem
        class="toolbox-menu-item-single"
        :subTitle="activeMeasurement"
        :title="
          isModelviewShowing
            ? t('general__text__measure-horizontal')
            : t('general__text__measure-horizontal-map')
        "
        :iconSource="resetIconSource"
        :slimMobile="true"
        @onIconClick="clearMeasurement"
      />
    </div>
    <div v-if="isDataComparisonToolActive && isMapviewShowing" class="toolbox-menu">
      <ToolboxMenuItem
        class="toolbox-menu-item-single"
        :title="t('general__text__data-visualization')"
      >
        <DropDownSelection
          activeState="active"
          cssClass="dark"
          :dropDownAbove="true"
          :dropDownTitle="selectedAreaName"
          :selectedID="selectedID"
          :options="AREA_TYPES"
          @selected="selectArea"
        />
        <br />
        <div>
          <p style="margin-bottom: 8px">
            <span id="mid-small" class="dot-small" />{{ t('general__text__interval') }}:
            {{ dataComparisonInterval.low.toLocaleString('de') }}–{{
              dataComparisonInterval.high.toLocaleString('de')
            }}
            {{
              currentDataComparison === t('general__data__area-efficiency-loa-h')
                ? '%'
                : t('general__data__unit-square-meter')
            }}
          </p>
        </div>
        <input
          id="dataComparisonIntervalLow"
          v-model="dataComparisonLow"
          class="input-data-comparison"
          style="margin-right: 14px"
          type="text"
          :placeholder="dataComparisonInterval.low.toLocaleString('de')"
          @blur="dataComparisonLow = ''"
          @keyup="handleDataComparisonIntervalInput"
        />
        <input
          id="dataComparisonIntervalHigh"
          v-model="dataComparisonHigh"
          class="input-data-comparison"
          type="text"
          :placeholder="dataComparisonInterval.high.toLocaleString('de')"
          @blur="dataComparisonHigh = ''"
          @keyup="handleDataComparisonIntervalInput"
        />
        <br />
        <thead>
          <tr>
            <td>
              <span id="below" class="dot" style="margin-left: 4px"></span>
            </td>
            <td>
              <span id="mid" class="dot"></span>
            </td>
            <td>
              <span id="above" class="dot"></span>
            </td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="dotText" style="padding-left: 2px; text-align: left">
              {{ t('general__text__below') }}
            </td>
            <td class="dotText">
              {{ t('general__text__within') }}
            </td>
            <td class="dotText">
              {{ t('general__text__above') }}
            </td>
          </tr>
        </tbody>
      </ToolboxMenuItem>
    </div>

    <div v-if="isToolboxMenuVisible" class="toolbox-menu">
      <div class="toolbox-temperature-container">
        <div class="toolbox-temperature-div">
          <div class="toolbox-temperature-div__small">
            <h4>{{ t('general__text__room') }}: {{ hoveredSensorData.room }}</h4>
          </div>
          <div class="toolbox-temperature-div__small">
            <div>
              <h4>{{ sensorTitle }} {{ formattedSensorText }}</h4>
            </div>
            <div class="tempCircle-div">
              <div
                v-if="sensorColor"
                :style="{ 'background-color': sensorColor }"
                class="tempCircle"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import _ from 'lodash'
import ButtonSquare from '../buttons/ButtonSquare/ButtonSquare.vue'
import DropDownSelection from '../../general/selectors/DropDownSelection.vue'
import RangeSliderSingle from '../selectors/Slider/RangeSliderSingle.vue'
import CustomRangeSlider from '../selectors/Slider/CustomRangeSlider.vue'
import ToolboxMenuItem from './ToolboxMenuItem.vue'
import { formatNumber } from '@/utils/math'
import {
  getSensorValueText,
  sensorTypeConfigs,
  SensorTypes,
  getColorForSensorValue,
  UNITS
} from '@/utils/format-sensor-values'
import { useStore } from 'vuex'
import { PLANVIEW_PREFIX, MAPVIEW_PREFIX } from '@/store/module-prefixes'
import {
  RESET_CURRENT_TOOL,
  SET_CURRENT_DATACOMPARISON,
  SET_CURRENT_DRAWING_TRANSPARENCY,
  SET_CURRENT_HEATMAP_INTENSITY,
  SET_CURRENT_MAP_DRAWING_TRANSPARENCY,
  SET_CURRENT_TOOL,
  SET_DATACOMPARISON_INTERVAL,
  SET_SELECTED_HEATMAP_DATE
} from '@/store/mutation-types'
import { useGeneralStore } from '@/pinia'
import { useI18n } from 'vue-i18n'
import crossIconSource from '@/assets/icon-cross.png'
import dataComparisonIconSource from '@/assets/icon-filter.svg'
import measureAreaIconSource from '@/assets/icon-measure-area.svg'
import measureLineIconSource from '@/assets/icon-measure-line.svg'
import resetIconSource from '@/assets/icon-reset--white.svg'
import toolsIconSource from '@/assets/icon-tools.svg'

type Props = {
  expanded: boolean
}

type Emits = (event: 'onToggleClick') => void

const emits = defineEmits<Emits>()
const props = withDefaults(defineProps<Props>(), {
  expanded: false
})

const { t } = useI18n()
const store = useStore()
const piniaStore = useGeneralStore()

const filteredDrawings = computed(() => piniaStore.filteredDrawings)
const selectedID = ref<string>()
const AREA_TYPES = [
  { name: 'BTA', value: 0, belongsToId: 'areaType00' },
  { name: 'BRA', value: 1, belongsToId: 'areaType01' },
  { name: 'LOA-h', value: 2, belongsToId: 'areaType02' },
  {
    name: t('general__data__area-efficiency-loa-h'),
    value: 3,
    id: 'areaType03'
  }
]

const dataComparisonHigh = ref('')
const dataComparisonLow = ref('')
const formattedSensorText = ref('')
const measurementTypeCleared = ref<string>()
const preventButtonInactive = ref(false)
const selectedArea = ref(0)
const selectedAreaName = ref('BTA')
const sensorColor = ref<string>()

const selectedLevel = computed(() => store.state.selectedLevel)
const isMapviewShowing = computed<boolean>(() => store.getters.isMapviewShowing)
const isModelviewShowing = computed<boolean>(() => store.getters.isModelviewShowing)
const isMeasurementActive = computed<boolean>(() => store.getters.isMeasurementActive)
const isDataComparisonToolActive = computed<boolean>(() => store.getters.isDataComparisonToolActive)
const isPlanviewShowing = computed<boolean>(() => store.getters.isPlanviewShowing)
const currentDataComparison = computed<string>(() => store.state.currentDataComparison)
const currentMeasurement = computed<number>(() => store.state.currentMeasurement)
const currentTool = computed<string>(() => store.state.currentTool)
const dataComparisonInterval = computed<{ high: number; low: number }>(
  () => store.state.dataComparisonInterval
)
const currentSensordataType = computed<SensorTypes>(
  () => store.state[PLANVIEW_PREFIX].currentSensordataType
)
const currentDrawingTransparency = computed<number>(
  () => store.state[PLANVIEW_PREFIX].currentDrawingTransparency
)
const currentHeatmapIntensity = computed<number>(
  () => store.state[PLANVIEW_PREFIX].currentHeatmapIntensity
)
const heatmapDateArray = computed<{ timeStamps: Date[] } | null>(
  () => store.state[PLANVIEW_PREFIX].heatmapDateArray
)
const hoveredSensorData = computed(() => store.state[PLANVIEW_PREFIX].hoveredSensorData)
const isSensordataShowing = computed<boolean>(
  () => store.state[PLANVIEW_PREFIX].isSensordataShowing
)

const sensorTitle = computed<string>(() => {
  if (currentSensordataType.value === 'booking') return ''
  return `${t(`general__data__sensor-${currentSensordataType.value}`)}:`
})

const isToolboxMenuVisible = computed<boolean>(() => {
  if (
    Object.keys(sensorTypeConfigs).includes(currentSensordataType.value) &&
    hoveredSensorData.value != null
  )
    return true
  return false
})

const formattedMeasurement = computed<string>(() => {
  if (currentMeasurement.value !== null) {
    return formatNumber(currentMeasurement.value, 2)
  } else {
    return '0'
  }
})

const activeMeasurement = computed<string>(() => {
  if (currentTool.value === 'areameasurement') {
    return (
      t('general__data__area') +
      ': ' +
      formattedMeasurement.value +
      ' ' +
      t('general__data__unit-square-meter')
    )
  } else if (currentTool.value === 'linemeasurement') {
    return t('general__data__distance') + ': ' + formattedMeasurement.value + ' m'
  } else {
    return t('general__data__reset')
  }
})

const activeMeasurementIcon = computed<string>(() => {
  switch (currentTool.value) {
    case 'areameasurement':
      return measureAreaIconSource
    case 'linemeasurement':
      return measureLineIconSource
    case 'datacomparisontool':
      if (isMapviewShowing.value) {
        return dataComparisonIconSource
      } else {
        return toolsIconSource
      }
    default:
      return toolsIconSource
  }
})

const buttonImgSource = computed<string>(() => {
  return props.expanded ? crossIconSource : activeMeasurementIcon.value
})

watch(
  () => _.cloneDeep(filteredDrawings.value),
  (newDrawing, oldDrawings) => {
    if (newDrawing && oldDrawings?.filter((drawing) => drawing.visible).length === 1) {
      setCurrentDrawingTransparency({ currentDrawingTransparency: 0.8 })
      setCurrentDrawingTransparencyMap({ currentDrawingTransparency: 0.8 })
    }
  }
)

watch(currentDataComparison, (n, o) => {
  if (n === t('general__data__area-efficiency-loa-h'))
    setDataComparisonInterval({
      comparisonInterval: { low: 70, high: 100 }
    })
  if (o === t('general__data__area-efficiency-loa-h'))
    setDataComparisonInterval({
      comparisonInterval: { low: 3000, high: 7000 }
    })
})

watch(hoveredSensorData, () => {
  if (!hoveredSensorData.value) return
  const currentValue = hoveredSensorData.value.value
  formattedSensorText.value =
    getSensorValueText(
      currentValue,
      currentSensordataType.value,
      UNITS[currentSensordataType.value]
    ) || ''
  sensorColor.value =
    currentValue !== undefined
      ? getColorForSensorValue(currentSensordataType.value, currentValue)
      : undefined
})

function resetTool() {
  store.commit(RESET_CURRENT_TOOL)
}

function setCurrentDataComparison(payload: { currentDataComparison: string }) {
  store.commit(SET_CURRENT_DATACOMPARISON, payload)
}

function setTool(payload: { tool: string }) {
  store.commit(SET_CURRENT_TOOL, payload)
}

function setDataComparisonInterval(payload: { comparisonInterval: { high: number; low: number } }) {
  store.commit(SET_DATACOMPARISON_INTERVAL, payload)
}

function setCurrentDrawingTransparency(payload: { currentDrawingTransparency: number }) {
  store.commit(PLANVIEW_PREFIX + '/' + SET_CURRENT_DRAWING_TRANSPARENCY, payload)
}

function setCurrentHeatmapIntensity(payload: { intensity: number }) {
  store.commit(PLANVIEW_PREFIX + '/' + SET_CURRENT_HEATMAP_INTENSITY, payload)
}

function setSelectedHeatmapDate(payload: { date: Date }) {
  store.commit(PLANVIEW_PREFIX + '/' + SET_SELECTED_HEATMAP_DATE, payload)
}

function setCurrentDrawingTransparencyMap(payload: { currentDrawingTransparency: number }) {
  store.commit(MAPVIEW_PREFIX + '/' + SET_CURRENT_MAP_DRAWING_TRANSPARENCY, payload)
}

function setCursor(cursorType: string) {
  // @ts-expect-error There's a bug in useMapbox that can initialize mapbox before the container is rendered, it is therefore not used here.
  store.map.getCanvas().style.cursor = cursorType
}

function changeHeatmapIntensity(value: number) {
  const intensity = value / 100
  setCurrentHeatmapIntensity({ intensity })
}

function clearMeasurement() {
  if (currentTool.value === 'linemeasurement') {
    preventButtonInactive.value = true
    measurementTypeCleared.value = 'linemeasurement'
    resetTool()
    setTimeout(() => {
      setTool({ tool: 'linemeasurement' })
    }, 500)
    setTimeout(() => {
      preventButtonInactive.value = false
      measurementTypeCleared.value = undefined
    }, 550)
  } else if (currentTool.value === 'areameasurement') {
    preventButtonInactive.value = true
    measurementTypeCleared.value = 'areameasurement'
    resetTool()
    setTimeout(() => {
      setTool({ tool: 'areameasurement' })
    }, 500)
    setTimeout(() => {
      preventButtonInactive.value = false
      measurementTypeCleared.value = undefined
    }, 550)
  }
}

function handleDataComparisonIntervalInput(e: KeyboardEvent) {
  if (e.keyCode === 13) {
    // Values changed when enter key is released
    let roundedLow: number = 0
    let roundedHigh: number = 0
    if (e.target instanceof HTMLElement && e.target.id === 'dataComparisonIntervalLow') {
      roundedLow = Math.round(parseFloat(dataComparisonLow.value.replace(',', '.')))
      roundedHigh = dataComparisonInterval.value.high
    } else if (e.target instanceof HTMLElement && e.target.id === 'dataComparisonIntervalHigh') {
      roundedLow = dataComparisonInterval.value.low
      roundedHigh = Math.round(parseFloat(dataComparisonHigh.value.replace(',', '.')))
    }
    if (isNaN(roundedLow) || isNaN(roundedHigh) || roundedLow < 0 || roundedHigh < 1) return
    if (
      currentDataComparison.value === t('general__data__area-efficiency-loa-h') &&
      (roundedLow > 99 || roundedHigh > 100)
    )
      return // Area efficiency is in percent and cannot exceed 100
    const comparisonInterval = { low: roundedLow, high: roundedHigh }
    if (roundedLow >= dataComparisonInterval.value.high) {
      comparisonInterval.high = roundedLow + 1 // Raise and add 1 to upper interval to prevent exceeding upper value
    }
    if (roundedHigh <= dataComparisonInterval.value.low) {
      comparisonInterval.low = roundedHigh - 1 // Lower and subtract 1 from lower interval to prevent overlapping interval min/max
    }
    setDataComparisonInterval({ comparisonInterval: comparisonInterval })
    dataComparisonLow.value = ''
    dataComparisonHigh.value = ''
  }
}

function onAreaMeasureButtonClick() {
  if (currentTool.value !== 'areameasurement') {
    resetTool()
  }
  if (!isMeasurementActive.value && currentTool.value !== 'areameasurement') {
    setTool({ tool: 'areameasurement' })
    emits('onToggleClick') // Closing menu on measurement selection
  } else {
    resetTool()
    setCursor('')
    emits('onToggleClick') // Closing menu if active measurement is selected (or "deselected")
  }
}

function onChangeHeatmapDate(index: number) {
  const date = heatmapDateArray.value?.timeStamps[index]
  if (date) setSelectedHeatmapDate({ date })
}

function onDataComparisonToolClick() {
  if (currentTool.value !== 'datacomparisontool') {
    resetTool()
  }
  if (!isDataComparisonToolActive.value && currentTool.value !== 'datacomparisontool') {
    setTool({ tool: 'datacomparisontool' })
    emits('onToggleClick') // Closing menu on measurement selection
  } else {
    resetTool()
    setCursor('')
    emits('onToggleClick') // Closing menu if active measurement is selected (or "deselected")
  }
}

function onLineMeasureButtonClick() {
  if (currentTool.value !== 'linemeasurement') {
    resetTool()
  }
  if (!isMeasurementActive.value && currentTool.value !== 'linemeasurement') {
    setTool({ tool: 'linemeasurement' })
    emits('onToggleClick') // Closing menu on measurement selection
  } else {
    resetTool()
    setCursor('')
    emits('onToggleClick') // Closing menu if active measurement is selected (or "deselected")
  }
}

function onToolButtonClick() {
  emits('onToggleClick')
  if (
    (isMeasurementActive.value && props.expanded) ||
    (isDataComparisonToolActive.value && props.expanded)
  ) {
    // Resetting tool if active when menu is closed
    resetTool()
    setCursor('')
  }
}

function rangeSliderValue(value: number) {
  const transparency = value / 100
  if (isMapviewShowing.value) {
    setCurrentDrawingTransparencyMap({ currentDrawingTransparency: transparency })
  } else {
    setCurrentDrawingTransparency({ currentDrawingTransparency: transparency })
  }
}

function selectArea(value: number, name: string, id: string) {
  selectedID.value = id
  selectedArea.value = value
  selectedAreaName.value = name
  setCurrentDataComparison({ currentDataComparison: name })
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/styles/variables.scss' as old-variables;

.button-square {
  margin-right: old-variables.$margin-small;
  margin-bottom: old-variables.$margin-small;
}

.parent {
  position: relative;
}

.closingCross {
  position: absolute;
  top: 10px;
  right: 0;
}

.input-data-comparison {
  background: old-variables.$color-grey-blue-darker
    url('../../../assets/icons-general/icon-edit.svg') no-repeat right 15px center;
  background-size: 16px;
  border: none;
  border-radius: old-variables.$border-radius-medium;
  box-shadow: old-variables.$box-shadow-button-and-search-field;
  color: old-variables.$color-text-white;
  display: inline-flex;
  font-family: old-variables.$font-family-general-paragraph;
  font-size: old-variables.$font-size-general-paragraph;
  font-weight: old-variables.$font-weight-general-paragraph;
  height: old-variables.$size-button-medium;
  outline: none;
  padding: old-variables.$margin-navigation-menu old-variables.$margin-navigation-menu
    old-variables.$margin-navigation-menu 15px;
  width: calc(50% - 7px);

  &::-webkit-input-placeholder {
    color: old-variables.$color-text-white;
  }

  &::-moz-placeholder {
    color: old-variables.$color-text-white;
  }

  &:focus::-webkit-input-placeholder {
    color: old-variables.$color-text-white-45;
  }

  &:focus::-moz-placeholder {
    color: old-variables.$color-text-white-45;
  }
}

.range-slider {
  padding-bottom: old-variables.$margin-small;
}

.toolbox-column {
  display: flex;
  flex-direction: column;
  position: absolute;
  right: 14px;
  z-index: variables.$z-index-map-toolbox;
}

.toolbox-menu {
  margin-top: old-variables.$margin-small;
  background-color: old-variables.$color-background-navigation-menu;
  padding: old-variables.$margin-small;
  border-radius: old-variables.$margin-small;
  width: calc(#{old-variables.$width-app-navigation-menu} - 50px);
  min-width: 300px;

  &__buttons {
    margin-left: 0;
  }

  &--collapsed {
    background-color: transparent;
    height: old-variables.$size-button-medium;
    overflow: visible;
    width: old-variables.$size-button-medium;
    min-width: old-variables.$size-button-medium;
    padding: 0;
    margin: calc(#{old-variables.$margin-small} + #{old-variables.$margin-small})
      old-variables.$margin-small old-variables.$margin-small auto;
  }

  &-item-single {
    margin-top: 0;

    &__additional {
      margin-top: old-variables.$margin-small;
    }
  }

  &__toggle {
    margin-right: 0;
  }

  &__header {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    height: old-variables.$size-button-medium;

    &__toggle-button {
      margin-right: -14px;
    }

    &__tool-buttons {
      display: flex;
      margin-left: 0;
    }

    &--collapsed {
      width: old-variables.$size-button-medium;
      min-width: old-variables.$size-button-medium;
      padding: 0;
      margin-left: auto;
      margin-right: 0;
    }
  }

  &__content {
    height: 100%;
    overflow: scroll;
    width: calc(#{old-variables.$width-app-navigation-menu} - 50px);
    min-width: 190px;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.toolbox-temperature-container {
  background-color: old-variables.$color-grey-blue-dark;
  border-radius: old-variables.$border-radius-small;
  color: old-variables.$color-text-white-70;

  .toolbox-temperature-div {
    padding: 0 old-variables.$margin-small;

    &__small {
      display: flex;
    }

    h4 {
      color: old-variables.$color-text-white-70;
      font-weight: old-variables.$font-weight-general-paragraph;
    }
  }
}

.tempCircle-div {
  display: flex;
  justify-content: center;
  align-items: center;
}

.tempCircle {
  margin-left: 20px;
  border: solid 2px old-variables.$color-grey-lighter;
  border-radius: 50%;
  height: 18px;
  width: 18px;
}

.dot {
  height: 35px;
  width: 35px;
  margin: 18px 14px 0 14px;
  border-radius: 50%;
  display: inline-block;

  &-small {
    border-radius: 50%;
    display: inline-block;
    height: 16px;
    margin: 0 7px;
    vertical-align: text-top;
    width: 16px;
  }
}

.dotText {
  color: old-variables.$color-text-white-45;
  font-size: old-variables.$font-size-general-paragraph;
  margin: 5px 15px 5px 15px;
  padding-bottom: 14px;
  padding-top: 4px;
  text-align: center;
}

#below {
  background-color: #73b3d5;
}

#mid,
#mid-small {
  background-color: #5aea8a;
}

#above {
  background-color: rgb(203, 101, 101);
}

@media screen and (max-width: 1024px) {
  .toolbox-column {
    right: old-variables.$margin-small;
  }
}

@media screen and (max-width: 658px) {
  .toolbox-menu {
    background-color: transparent;
    padding: 0;
    margin-top: 14px;
    margin-right: 12px;

    &-item-single {
      justify-content: space-around;

      &__additional {
        padding-bottom: old-variables.$margin-small;
        margin-top: old-variables.$margin-small;
      }
    }

    &__header {
      justify-content: unset;
      margin-top: 14px;

      &__toggle-button {
        margin-right: 0;
      }

      &__tool-buttons {
        background-color: #ffffff;
        border-radius: old-variables.$border-radius-medium;
        margin-right: old-variables.$margin-small;
      }

      &--collapsed {
        min-width: unset;
      }
    }
  }
}
</style>
